import { defineStore } from "pinia";
import { ref } from "vue";
import { authRepository } from "../../data/repositories/authRepository";

export type OtpMethod = "email" | "whatsapp";

const STORAGE_KEY_PREFIX = "mfa_otp_expires_";

export const useMfaMethodStore = defineStore("mfaMethodStore", () => {
  const isLoading = ref<boolean>(false);
  const errorMessage = ref<string | null>(null);
  const clientName = ref<string>("");
  const clientDescription = ref<string>("");
  const clientLogo = ref<string | null>(null);
  const isClientValid = ref<boolean>(true);

  const mfaToken = ref<string>("");
  const availableMethods = ref<OtpMethod[]>([]);
  const isSubmitting = ref<boolean>(false);

  const methodCountdowns = ref<Record<string, number>>({});
  let timers: Record<string, any> = {};

  async function validateClient(clientId: string, redirectUri: string) {
    isLoading.value = true;
    try {
      const response = await authRepository.validateClient(clientId, redirectUri);
      isClientValid.value = response.isValid;
      clientName.value = response.client.name;
      clientDescription.value = response.client.description ?? "";
      clientLogo.value = response.client.logo || null;
    } catch (error: any) {
      isClientValid.value = false;
      errorMessage.value = error.response?.data?.message || "Gagal memvalidasi klien.";
    } finally {
      isLoading.value = false;
    }
  }

  function init(token: string, methods: OtpMethod[]) {
    // Bersihkan semua interval timer aktif sebelumnya agar tidak bocor (memory leak)
    Object.keys(timers).forEach((m) => {
      clearInterval(timers[m]);
    });
    timers = {};
    methodCountdowns.value = {};

    mfaToken.value = token;
    availableMethods.value = methods;

    // Refresh dan sinkronkan sisa countdown dari localStorage
    checkActiveCountdowns(token);
  }

  function checkActiveCountdowns(token: string) {
    const storageKey = STORAGE_KEY_PREFIX + token;
    const savedExpiresAt = localStorage.getItem(storageKey);
    const now = Date.now();

    if (savedExpiresAt) {
      const expiresAt = parseInt(savedExpiresAt, 10);
      const diffSec = Math.floor((expiresAt - now) / 1000);
      if (diffSec > 0) {
        availableMethods.value.forEach((m) => {
          methodCountdowns.value[m] = diffSec;
          startMethodCountdown(m, diffSec, token);
        });
      } else {
        localStorage.removeItem(storageKey);
      }
    }
  }

  function startMethodCountdown(method: OtpMethod, duration: number, token: string) {
    if (timers[method]) clearInterval(timers[method]);

    methodCountdowns.value[method] = duration;

    timers[method] = setInterval(() => {
      if (methodCountdowns.value[method] > 0) {
        methodCountdowns.value[method]--;
      } else {
        clearInterval(timers[method]);
        delete methodCountdowns.value[method];
        localStorage.removeItem(STORAGE_KEY_PREFIX + token);
      }
    }, 1000);
  }

  async function selectMethod(method: OtpMethod, onSuccess: () => void) {
    if (methodCountdowns.value[method] && methodCountdowns.value[method] > 0) {
      window.$message?.warning("Tunggu sebentar ya, kode OTP baru bisa dikirim ulang setelah timer habis.");
      return;
    }

    isSubmitting.value = true;
    errorMessage.value = null;

    try {
      await authRepository.requestOtp({
        mfaToken: mfaToken.value,
        method,
      });
      window.$message?.success(`Kode OTP berhasil dikirim ke ${method}.`);

      const expiresAt = Date.now() + 180 * 1000;
      localStorage.setItem(STORAGE_KEY_PREFIX + mfaToken.value, expiresAt.toString());

      onSuccess();
    } catch (error: any) {
      errorMessage.value = error.response?.data?.message || "Gagal mengirim kode OTP.";
      window.$message?.warning(errorMessage.value!);
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    isLoading,
    errorMessage,
    clientName,
    clientDescription,
    clientLogo,
    isClientValid,
    mfaToken,
    availableMethods,
    isSubmitting,
    methodCountdowns,
    validateClient,
    init,
    selectMethod,
  };
});
